import { Description, Label, TextArea, TextField } from "@heroui/react";
import { memo, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

type TextLongProps = {
  question: string;
  description?: string;
  placeholder?: string;
  initialValue?: string;
  /** Soft cap; characters above this don't block input but show a count warning. */
  softCap?: number;
  /** Hard cap; truncates input. */
  maxLength?: number;
  examples?: string[];
  onChange?: (value: string) => void;
  className?: string;
  testId?: string;
};

const TextLongImpl = ({
  question,
  description,
  placeholder = "Tell us more…",
  initialValue = "",
  softCap = 240,
  maxLength = 600,
  examples,
  onChange,
  className,
  testId,
}: TextLongProps) => {
  const [value, setValue] = useState(initialValue);

  const handleChange = (next: string): void => {
    setValue(next);
    onChange?.(next);
  };

  const overSoftCap = value.length > softCap;

  return (
    <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />

      {examples && examples.length > 0 && (
        <div
          className="rounded-xl p-3 text-xs"
          style={{
            background: "hsl(var(--brand-bg-foreground) / 0.04)",
            color: "hsl(var(--hero-fg-muted))",
          }}
        >
          <strong>Examples:</strong>
          <ul className="mt-1.5 list-disc pl-4">
            {examples.map((example, i) => (
              <li key={i}>{example}</li>
            ))}
          </ul>
        </div>
      )}

      <TextField value={value} onChange={handleChange}>
        <Label className="sr-only">{question}</Label>
        <TextArea
          rows={4}
          placeholder={placeholder}
          maxLength={maxLength}
          className="resize-none overflow-auto"
        />
        <Description
          className={`text-right text-xs tabular-nums ${
            overSoftCap ? "text-[hsl(var(--hero-warning))]" : ""
          }`}
        >
          {value.length} / {softCap}
          {overSoftCap && ` (max ${maxLength})`}
        </Description>
      </TextField>
    </section>
  );
};

export const TextLong = memo(TextLongImpl);

export default TextLong;
